import { inject, Injectable } from '@angular/core';
import { ActivityInterface, AutomationInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable } from 'rxjs';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';
import { YeswareCampaignSelectorService } from '../../../../data-services/yesware-campaign-selector.service';
import { map, switchMap, take } from 'rxjs/operators';
import { AutomationsRouterService } from '../../../../shared/automations-router.service';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example AddTagService
 */
@Injectable({ providedIn: 'root' })
export class StartYeswareCampaignService extends AutomationDisplayService {
  private readonly campaignsService: YeswareCampaignSelectorService = inject(YeswareCampaignSelectorService);
  private readonly router: AutomationsRouterService = inject(AutomationsRouterService);

  constructor() {
    super();
  }

  getActivityColumn(
    namespace: string,
    activity: ActivityInterface,
    automation?: AutomationInterface,
  ): ActivityTableEntry {
    if (activity?.error) {
      return this.tableEntry(
        this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_YESWARE_CAMPAIGN.ACTIVITY.FAILED'),
        this.getDefaultStepColumnBody(namespace, activity.workflowStep, automation),
      );
    }

    return this.tableEntry(
      this.router.applyRoutePrefix(`/crm/contact/profile/${activity.entityId}`).pipe(
        take(1),
        map((linkToContact) =>
          this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_YESWARE_CAMPAIGN.ACTIVITY.SUCCESS', {
            url: linkToContact,
          }),
        ),
      ),
      this.getDefaultStepColumnBody(namespace, activity.workflowStep, automation),
    );
  }

  getAutomationNodeData(
    _namespace: string,
    _step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
    _automation?: AutomationInterface,
  ): Observable<NodeData> {
    let campaignId = '';
    try {
      const parsedData = JSON.parse(_step.data as string);
      campaignId = parsedData.campaign_id;
    } catch (e) {
      console.error('Error parsing campaign data:', e);
    }
    return this.campaignsService.getMulti([campaignId]).pipe(
      switchMap((campaigns) => {
        if (campaigns.length > 0) {
          return this.nodeDisplayData(
            this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_YESWARE_CAMPAIGN.ACTION', {
              campaignName: campaigns[0].name,
            }),
            this.noValidationErrorsDetected(),
          );
        }
        return this.nodeDisplayData(
          this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_YESWARE_CAMPAIGN.ACTION', {
            campaignName: '',
          }),
          this.validationErrorsDetected(
            _step,
            this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_YESWARE_CAMPAIGN.NOT_FOUND'),
            true,
          ),
        );
      }),
    );
  }
}
